'use client';

import type { FC } from 'react';
import { useMemo, useRef } from 'react';
import { BottomSheet, EmptyState, Skeleton, Spinner } from '../../components/ui';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Credits } from '../../components/Credits';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { formatDateNumeric } from '../../i18n/formatDate';
import { useUpgradeHistoryInfinite } from '../../queries/useUpgradeQueries';
import type { UpgradeHistoryItem } from '../../services/api';
import translations from './UpgradeScreen.translations';
import { UpgradePairLine } from './UpgradePairLine';

interface UpgradeHistorySheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const HistoryRow: FC<{ item: UpgradeHistoryItem; lang: Parameters<typeof formatDateNumeric>[1] }> = ({ item, lang }) => {
  const { t } = useLocalTranslation(translations);
  return (
    <li data-testid="upgrade-history-row" className="flex w-full flex-col gap-2.5 border-b border-white/5 py-3 last:border-b-0">
      <UpgradePairLine
        source={{ name: item.sourceItemName, imageUrl: item.sourceItemImage }}
        sourceAmount={item.sourceAmount}
        target={{ name: item.targetItemName, imageUrl: item.targetItemImage }}
        footer={
          <span dir="ltr" className="mt-0.5 self-start whitespace-nowrap text-xs tabular-nums">
            <span className="font-semibold text-accent-green">{(item.probability * 100).toFixed(1)}%</span>
            <span className="mx-1 text-white/30">·</span>
            <span className="font-semibold text-accent-blue">×{item.coefficient.toFixed(2)}</span>
          </span>
        }
      />
      <div className="flex items-center justify-between gap-3 text-xs">
        <span className="whitespace-nowrap tabular-nums text-white/40">{formatDateNumeric(item.createdAt, lang, { time: true })}</span>
        {item.isSuccess ? (
          <span className="flex items-center gap-1.5 whitespace-nowrap text-white/50">
            {t('historyWin')}
            <Credits amount={item.targetItemPrice} iconSize={11} className="font-semibold text-white" />
          </span>
        ) : (
          <span className="text-white/40">{t('historyLoss')}</span>
        )}
      </div>
    </li>
  );
};

export const UpgradeHistorySheet: FC<UpgradeHistorySheetProps> = ({ isOpen, onClose }) => {
  const { t, lang } = useLocalTranslation(translations);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { data, isLoading, isError, refetch, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useUpgradeHistoryInfinite(isOpen);

  const items = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

  useInfiniteScroll({
    hasMore: hasNextPage,
    loading: isFetchingNextPage,
    onLoadMore: () => void fetchNextPage(),
    threshold: 200,
    scrollRef,
  });

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={t('history')} scrollRef={scrollRef}>
      {isLoading ? (
        <div className="flex flex-col gap-3 px-4 py-3">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-[72px] w-full rounded-xl" />
          ))}
        </div>
      ) : isError ? (
        <ErrorMessage error={t('errorLoadHistory')} onRetry={() => void refetch()} />
      ) : items.length === 0 ? (
        <EmptyState title={t('historyEmpty')} />
      ) : (
        <>
          <ul className="m-0 list-none px-4 py-1">
            {items.map((item) => (
              <HistoryRow key={item.id} item={item} lang={lang} />
            ))}
          </ul>
          {isFetchingNextPage && (
            <div className="flex justify-center p-5">
              <Spinner size="s" />
            </div>
          )}
        </>
      )}
    </BottomSheet>
  );
};
